import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import {
  content,
  getCardPresentation,
  validateConstructedDeck,
} from '@cards/cards';
import type { DeckPassiveId } from '@cards/cards';
import type { CardDefId } from '@cards/engine';
import {
  parseAssistantChatResponse,
  type AssistantCatalogAction,
  type AssistantDeckSuggestion,
  type AssistantPageId,
  type AssistantSandboxAction,
  type AssistantSandboxContext,
} from '../../server/assistant/contracts.js';
import {
  ASSISTANT_PAGE_KNOWLEDGE,
  assistantPageElement,
} from '../../server/assistant/knowledge.js';
import { AssistantMarkdown } from './AssistantMarkdown.js';
import { AssistantGlyph } from './AssistantGlyph.js';
import { Button } from '../ui/kit/index.js';
import { cardTypeLabel } from '../ui/locale.js';
import {
  analyzeAssistantDeck,
  sameAssistantDeckSnapshot,
  type AssistantDeckSnapshot,
} from './deckAnalysis.js';

const THREAD_STORAGE_KEY = 'cards:assistant:thread:v1';
const PANEL_STORAGE_KEY = 'cards:assistant:panel:v1';
const MAX_STORED_MESSAGES = 30;
const PANEL_MIN_WIDTH = 300;
const PANEL_MIN_HEIGHT = 360;
const PANEL_MAX_WIDTH = 400;
const PANEL_MAX_HEIGHT = 620;
const PANEL_VIEWPORT_MARGIN = 12;
const MOBILE_SHEET_MIN_RATIO = 0.38;
const MOBILE_SHEET_DEFAULT_RATIO = 0.72;
const MOBILE_SHEET_MIN_HEIGHT = 240;
const MOBILE_SHEET_DISMISS_DISTANCE = 64;
const MOBILE_SHEET_DRAG_FLOOR = 96;
let fallbackIdSequence = 0;

interface StoredAssistantMessage {
  readonly id: string;
  readonly role: 'user' | 'assistant';
  readonly content: string;
  readonly page: AssistantPageId;
  readonly mode?: 'ui' | 'deck';
  readonly focusElementId?: string | null;
  readonly catalogAction?: AssistantCatalogAction | null;
  readonly deckSuggestion?: AssistantDeckSuggestion | null;
  readonly deckBase?: AssistantDeckSnapshot;
  readonly sandboxAction?: AssistantSandboxAction | null;
  readonly deckId?: string;
}

interface PanelPosition {
  readonly x: number;
  readonly y: number;
}

interface PanelSize {
  readonly width: number;
  readonly height: number;
}

interface DeckEditorBridge {
  readonly deckId: string;
  readonly name: string;
  readonly cardIds: readonly CardDefId[];
  readonly passiveId: DeckPassiveId | null;
  readonly locked: boolean;
  readonly apply: (
    cardIds: readonly CardDefId[],
    passiveId: DeckPassiveId | null,
  ) => boolean;
  readonly undo?: Readonly<{
    applied: AssistantDeckSnapshot;
    run: () => boolean;
  }>;
}

interface DeckEditorRegistration {
  readonly active: boolean;
  readonly deckId: string;
  readonly name: string;
  readonly cardIds: readonly CardDefId[];
  readonly passiveId: DeckPassiveId | null;
  readonly locked: boolean;
  readonly onApply: (
    cardIds: readonly CardDefId[],
    passiveId: DeckPassiveId | null,
  ) => boolean;
  readonly undo?: Readonly<{
    applied: AssistantDeckSnapshot;
    onUndo: () => boolean;
  }>;
}

interface CardCatalogBridge {
  readonly page: 'catalog' | 'deck_editor' | 'sandbox';
  readonly apply: (action: AssistantCatalogAction) => boolean;
}

interface CardCatalogRegistration {
  readonly active: boolean;
  readonly page: 'catalog' | 'deck_editor' | 'sandbox';
  readonly onApply: (action: AssistantCatalogAction) => boolean;
}

interface SandboxBridge {
  readonly context: AssistantSandboxContext;
  readonly canApply: boolean;
  readonly apply: (action: AssistantSandboxAction) => boolean;
}

interface SandboxRegistration {
  readonly active: boolean;
  readonly context: AssistantSandboxContext;
  readonly canApply: boolean;
  readonly onApply: (action: AssistantSandboxAction) => boolean;
}

type RegisterDeckEditor = (bridge: DeckEditorBridge | undefined) => void;
type RegisterCardCatalog = (bridge: CardCatalogBridge | undefined) => void;
type RegisterSandbox = (bridge: SandboxBridge | undefined) => void;

const DeckEditorAssistantContext = createContext<RegisterDeckEditor | undefined>(undefined);
const CardCatalogAssistantContext = createContext<RegisterCardCatalog | undefined>(undefined);
const SandboxAssistantContext = createContext<RegisterSandbox | undefined>(undefined);

function id(): string {
  return globalThis.crypto?.randomUUID?.()
    ?? `${Date.now().toString(36)}-${(fallbackIdSequence += 1).toString(36)}`;
}

function safeGet(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    // The assistant remains usable for the current page when storage is blocked.
  }
}

function isPageId(value: unknown): value is AssistantPageId {
  return typeof value === 'string'
    && Object.prototype.hasOwnProperty.call(ASSISTANT_PAGE_KNOWLEDGE, value);
}

function loadMessages(): readonly StoredAssistantMessage[] {
  const raw = safeGet(THREAD_STORAGE_KEY);
  if (raw === null) return [];
  try {
    const value = JSON.parse(raw) as unknown;
    if (!Array.isArray(value)) return [];
    return value.slice(-MAX_STORED_MESSAGES).flatMap((item): StoredAssistantMessage[] => {
      if (typeof item !== 'object' || item === null || Array.isArray(item)) return [];
      const object = item as Record<string, unknown>;
      if ((object.role !== 'user' && object.role !== 'assistant')
        || typeof object.id !== 'string'
        || typeof object.content !== 'string'
        || object.content.length === 0
        || object.content.length > 6_000
        || !isPageId(object.page)) return [];
      let message: StoredAssistantMessage = {
        id: object.id,
        role: object.role,
        content: object.content,
        page: object.page,
        ...(object.mode === 'ui' || object.mode === 'deck' ? { mode: object.mode } : {}),
        ...(typeof object.focusElementId === 'string' || object.focusElementId === null
          ? { focusElementId: object.focusElementId }
          : {}),
        ...(typeof object.deckId === 'string' ? { deckId: object.deckId } : {}),
      };
      if (object.catalogAction === null) {
        message = { ...message, catalogAction: null };
      } else if (typeof object.catalogAction === 'object'
        && object.catalogAction !== null
        && !Array.isArray(object.catalogAction)) {
        const action = object.catalogAction as Record<string, unknown>;
        const storedCardIds = Array.isArray(action.cardIds)
          && action.cardIds.length <= 20
          && action.cardIds.every((cardId) =>
            typeof cardId === 'string'
            && cardId.trim().length > 0
            && cardId.length <= 256)
          ? [...new Set(action.cardIds as string[])]
          : typeof action.cardId === 'string'
            ? [action.cardId]
            : action.cardId === null
              ? []
              : undefined;
        if (typeof action.query === 'string'
          && action.query.trim().length > 0
          && action.query.length <= 120
          && storedCardIds !== undefined) {
          message = {
            ...message,
            catalogAction: {
              query: action.query,
              cardIds: storedCardIds,
            },
          };
        }
      }
      if (object.deckSuggestion === null) {
        message = { ...message, deckSuggestion: null };
      } else if (typeof object.deckSuggestion === 'object'
        && object.deckSuggestion !== null
        && !Array.isArray(object.deckSuggestion)) {
        const suggestion = object.deckSuggestion as Record<string, unknown>;
        if (typeof suggestion.title === 'string'
          && typeof suggestion.summary === 'string'
          && Array.isArray(suggestion.cardIds)
          && suggestion.cardIds.length === 12
          && suggestion.cardIds.every((cardId) => typeof cardId === 'string')
          && (suggestion.passiveId === undefined
            || suggestion.passiveId === null
            || typeof suggestion.passiveId === 'string')) {
          message = {
            ...message,
            deckSuggestion: {
              title: suggestion.title,
              summary: suggestion.summary,
              cardIds: suggestion.cardIds as string[],
              passiveId: typeof suggestion.passiveId === 'string'
                ? suggestion.passiveId
                : null,
            },
          };
        }
      }
      if (typeof object.deckBase === 'object'
        && object.deckBase !== null
        && !Array.isArray(object.deckBase)) {
        const base = object.deckBase as Record<string, unknown>;
        if (Array.isArray(base.cardIds)
          && base.cardIds.length <= 12
          && base.cardIds.every((cardId) => typeof cardId === 'string')
          && (base.passiveId === null || typeof base.passiveId === 'string')) {
          message = {
            ...message,
            deckBase: {
              cardIds: base.cardIds as CardDefId[],
              passiveId: base.passiveId as DeckPassiveId | null,
            },
          };
        }
      }
      if (object.sandboxAction === null) {
        message = { ...message, sandboxAction: null };
      } else if (object.sandboxAction !== undefined) {
        try {
          const parsed = parseAssistantChatResponse({
            mode: 'ui',
            reply: {
              message: 'stored',
              focusElementId: null,
              catalogAction: null,
              deckSuggestion: null,
              sandboxAction: object.sandboxAction,
            },
          });
          message = { ...message, sandboxAction: parsed.reply.sandboxAction };
        } catch { /* устаревшее или повреждённое действие не восстанавливаем */ }
      }
      return [message];
    });
  } catch {
    return [];
  }
}

function loadPanelState(): {
  open: boolean;
  position?: PanelPosition;
  size?: PanelSize;
  mobileHeight?: number;
} {
  const raw = safeGet(PANEL_STORAGE_KEY);
  if (raw === null) return { open: false };
  try {
    const value = JSON.parse(raw) as Record<string, unknown>;
    const open = value.open === true;
    const size = typeof value.width === 'number' && Number.isFinite(value.width)
      && typeof value.height === 'number' && Number.isFinite(value.height)
      ? clampSize({ width: value.width, height: value.height })
      : undefined;
    const mobileHeight = typeof value.mobileHeight === 'number'
      && Number.isFinite(value.mobileHeight)
      ? clampMobileSheetHeight(value.mobileHeight)
      : undefined;
    if (typeof value.x === 'number' && Number.isFinite(value.x)
      && typeof value.y === 'number' && Number.isFinite(value.y)) {
      const position = clampPosition(
        { x: value.x, y: value.y },
        size ?? maximumPanelSize(),
      );
      return {
        open,
        position,
        ...(size === undefined ? {} : { size }),
        ...(mobileHeight === undefined ? {} : { mobileHeight }),
      };
    }
    return {
      open,
      ...(size === undefined ? {} : { size }),
      ...(mobileHeight === undefined ? {} : { mobileHeight }),
    };
  } catch {
    return { open: false };
  }
}

function currentPage(): AssistantPageId {
  const pathname = globalThis.location?.pathname ?? '/';
  if (pathname === '/lab/sandbox' || pathname === '/lab/sandbox/') return 'sandbox';
  if (/^\/decks\/[^/]+$/u.test(pathname)) return 'deck_editor';
  if (pathname === '/decks') return 'deck_library';
  if (pathname === '/lab' || pathname === '/lab/') return 'catalog';
  if (document.querySelector('[data-testid="onboarding-starter-provisioning"]') !== null) {
    return 'onboarding_deck';
  }
  if (document.querySelector('[data-testid="game-view"]') !== null) return 'game';
  if (document.querySelector('[data-testid="matchmaking-search"]') !== null) return 'matchmaking';
  // Only the legacy preset surface is left: the saved-deck seat applies the deck
  // chosen on the home page and renders a loader, never a page to assist with.
  if (document.querySelector('[data-testid="deck-selection"]') !== null) return 'deck_selection';
  if (document.querySelector('[data-testid="lobby"]') !== null) return 'lobby';
  return 'unknown';
}

function visibleElementIds(page: AssistantPageId): readonly string[] {
  return ASSISTANT_PAGE_KNOWLEDGE[page].elements
    .filter(({ selector }) => {
      try {
        const element = document.querySelector<HTMLElement>(selector);
        if (element === null) return false;
        const style = globalThis.getComputedStyle?.(element);
        return style === undefined || (style.display !== 'none' && style.visibility !== 'hidden');
      } catch {
        return false;
      }
    })
    .map(({ id: elementId }) => elementId);
}

function maximumPanelSize(): PanelSize {
  return {
    width: Math.min(PANEL_MAX_WIDTH, Math.max(0, globalThis.innerWidth - PANEL_VIEWPORT_MARGIN * 2)),
    height: Math.min(PANEL_MAX_HEIGHT, Math.max(0, globalThis.innerHeight - PANEL_VIEWPORT_MARGIN * 2)),
  };
}

function clampSize(size: PanelSize): PanelSize {
  const maximum = maximumPanelSize();
  return {
    width: Math.max(
      Math.min(PANEL_MIN_WIDTH, maximum.width),
      Math.min(size.width, maximum.width),
    ),
    height: Math.max(
      Math.min(PANEL_MIN_HEIGHT, maximum.height),
      Math.min(size.height, maximum.height),
    ),
  };
}

function clampPosition(position: PanelPosition, size: PanelSize): PanelPosition {
  return {
    x: Math.max(
      PANEL_VIEWPORT_MARGIN,
      Math.min(position.x, globalThis.innerWidth - size.width - PANEL_VIEWPORT_MARGIN),
    ),
    y: Math.max(
      PANEL_VIEWPORT_MARGIN,
      Math.min(position.y, globalThis.innerHeight - size.height - PANEL_VIEWPORT_MARGIN),
    ),
  };
}

function mobileSheetMetrics(): {
  readonly minimum: number;
  readonly defaultHeight: number;
  readonly maximum: number;
  readonly points: readonly number[];
} {
  const maximum = Math.max(0, globalThis.innerHeight - PANEL_VIEWPORT_MARGIN);
  const minimum = Math.min(
    maximum,
    Math.max(
      Math.min(MOBILE_SHEET_MIN_HEIGHT, maximum),
      Math.round(globalThis.innerHeight * MOBILE_SHEET_MIN_RATIO),
    ),
  );
  const defaultHeight = Math.min(
    maximum,
    Math.max(minimum, Math.round(globalThis.innerHeight * MOBILE_SHEET_DEFAULT_RATIO)),
  );
  return {
    minimum,
    defaultHeight,
    maximum,
    points: [...new Set([minimum, defaultHeight, maximum])],
  };
}

function clampMobileSheetHeight(height: number): number {
  const { minimum, maximum } = mobileSheetMetrics();
  return Math.max(minimum, Math.min(height, maximum));
}

function defaultMobileSheetHeight(): number {
  return mobileSheetMetrics().defaultHeight;
}

function nearestMobileSheetHeight(height: number): number {
  const { points } = mobileSheetMetrics();
  return points.reduce((nearest, point) =>
    Math.abs(point - height) < Math.abs(nearest - height) ? point : nearest,
  points[0] ?? 0);
}

function highlight(page: AssistantPageId, elementId: string): boolean {
  const knowledge = assistantPageElement(page, elementId);
  if (knowledge === undefined) return false;
  let element: HTMLElement | null;
  try {
    element = document.querySelector<HTMLElement>(knowledge.selector);
  } catch {
    return false;
  }
  if (element === null) return false;
  highlightElement(element);
  return true;
}

function highlightElement(element: HTMLElement): void {
  highlightElements([element]);
}

function highlightElements(elements: readonly HTMLElement[]): void {
  if (elements.length === 0) return;
  const first = elements[0];
  if (typeof first?.scrollIntoView === 'function') {
    first.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
  }
  for (const element of elements) element.classList.remove('cards-ai-highlight');
  const add = (): void => {
    for (const element of elements) element.classList.add('cards-ai-highlight');
  };
  if (globalThis.requestAnimationFrame === undefined) add();
  else globalThis.requestAnimationFrame(add);
  globalThis.setTimeout(() => {
    for (const element of elements) element.classList.remove('cards-ai-highlight');
  }, 5_000);
}

function elementByTestId(testId: string): HTMLElement | undefined {
  return [...document.querySelectorAll<HTMLElement>('[data-testid]')]
    .find((element) => element.dataset.testid === testId);
}

function afterCatalogRender(callback: () => void): void {
  if (globalThis.requestAnimationFrame === undefined) {
    globalThis.setTimeout(callback, 0);
    return;
  }
  globalThis.requestAnimationFrame(() => {
    globalThis.requestAnimationFrame(callback);
  });
}

function applyCatalogAction(
  bridge: CardCatalogBridge | undefined,
  action: AssistantCatalogAction,
): boolean {
  if (bridge === undefined || !bridge.apply(action)) return false;
  afterCatalogRender(() => {
    const searchElementId = bridge.page === 'catalog'
      ? 'catalog-search'
      : bridge.page === 'sandbox'
        ? 'sandbox-search'
        : 'deck-catalog-search';
    const searchKnowledge = assistantPageElement(bridge.page, searchElementId);
    const search = searchKnowledge === undefined
      ? null
      : document.querySelector<HTMLElement>(searchKnowledge.selector);
    search?.focus();
    if (action.cardIds.length === 0) {
      if (search !== null) highlightElement(search);
      return;
    }
    const cards = action.cardIds.flatMap((cardId) => {
      const card = elementByTestId(bridge.page === 'catalog'
        ? `catalog-card-info-${cardId}`
        : bridge.page === 'sandbox'
          ? `sandbox-catalog-${cardId}`
          : `deck-catalog-card-${cardId}`);
      return card === undefined ? [] : [card];
    });
    highlightElements(cards);
  });
  return true;
}

function deckCounts(cardIds: readonly string[]): readonly {
  id: string;
  name: string;
  count: number;
}[] {
  const counts = new Map<string, number>();
  for (const cardId of cardIds) counts.set(cardId, (counts.get(cardId) ?? 0) + 1);
  return [...counts].map(([cardId, count]) => ({
    id: cardId,
    name: getCardPresentation(cardId)?.name ?? cardId,
    count,
  }));
}

const DECK_ENERGY_FORMAT = new Intl.NumberFormat('ru-RU', {
  maximumFractionDigits: 2,
});

function deckChangeText(
  changes: readonly { readonly cardId: CardDefId; readonly count: number }[],
): string {
  return changes.map(({ cardId, count }) =>
    `${getCardPresentation(cardId)?.name ?? cardId} ×${count}`).join(', ');
}

function assistantErrorMessage(status: number, code: string | undefined): string {
  if (code === 'AI_MAINTENANCE') return 'AI сейчас на техническом обслуживании.';
  if (status === 429 || code === 'RATE_LIMITED' || code === 'AI_RATE_LIMITED') {
    return 'Слишком много запросов. Подождите немного и повторите.';
  }
  if (status === 504 || code === 'AI_TIMEOUT') {
    return 'Ответ занял слишком много времени. Попробуйте ещё раз.';
  }
  if (status === 404) {
    return 'ИИ-помощник пока не настроен на сервере.';
  }
  if (code === 'FORBIDDEN_ORIGIN') {
    return 'Эта страница открыта с адреса, которому сервер не разрешает запросы к помощнику.';
  }
  if (code === 'INVALID_REQUEST') {
    return 'Страница устарела после обновления. Перезагрузите её; если ошибка повторится, очистите чат помощника.';
  }
  return 'Не удалось получить ответ помощника. Проверьте соединение и повторите.';
}

export function useAssistantDeckEditor(registration: DeckEditorRegistration): void {
  const register = useContext(DeckEditorAssistantContext);
  const applyRef = useRef(registration.onApply);
  const undoRef = useRef(registration.undo?.onUndo);
  applyRef.current = registration.onApply;
  undoRef.current = registration.undo?.onUndo;
  const cardKey = registration.cardIds.join('\u0000');
  const undoKey = registration.undo === undefined
    ? ''
    : `${registration.undo.applied.cardIds.join('\u0000')}\u0001${registration.undo.applied.passiveId ?? ''}`;

  useEffect(() => {
    if (register === undefined || !registration.active) return undefined;
    const bridge: DeckEditorBridge = {
      deckId: registration.deckId,
      name: registration.name,
      cardIds: Object.freeze([...registration.cardIds]),
      passiveId: registration.passiveId,
      locked: registration.locked,
      apply: (cardIds, passiveId) => applyRef.current(cardIds, passiveId),
      ...(registration.undo === undefined ? {} : {
        undo: {
          applied: {
            cardIds: Object.freeze([...registration.undo.applied.cardIds]),
            passiveId: registration.undo.applied.passiveId,
          },
          run: () => undoRef.current?.() ?? false,
        },
      }),
    };
    register(bridge);
    return () => register(undefined);
  }, [
    register,
    registration.active,
    registration.deckId,
    registration.name,
    registration.passiveId,
    registration.locked,
    cardKey,
    undoKey,
  ]);
}

export function useAssistantCardCatalog(registration: CardCatalogRegistration): void {
  const register = useContext(CardCatalogAssistantContext);
  const applyRef = useRef(registration.onApply);
  applyRef.current = registration.onApply;

  useEffect(() => {
    if (register === undefined || !registration.active) return undefined;
    register({
      page: registration.page,
      apply: (action) => applyRef.current(action),
    });
    return () => register(undefined);
  }, [register, registration.active, registration.page]);
}

export function useAssistantSandbox(registration: SandboxRegistration): void {
  const register = useContext(SandboxAssistantContext);
  const applyRef = useRef(registration.onApply);
  applyRef.current = registration.onApply;
  const contextKey = JSON.stringify(registration.context);

  useEffect(() => {
    if (register === undefined || !registration.active) return undefined;
    register({
      context: registration.context,
      canApply: registration.canApply,
      apply: (action) => applyRef.current(action),
    });
    return () => register(undefined);
  }, [register, registration.active, registration.canApply, contextKey]);
}

function AssistantPanel({
  page,
  messages,
  open,
  position,
  size,
  mobileHeight,
  loading,
  enabled,
  error,
  input,
  deckBridge,
  catalogBridge,
  sandboxBridge,
  onInput,
  onOpen,
  onClose,
  onClear,
  onCancel,
  onSubmit,
  onPosition,
  onSize,
  onMobileHeight,
  onCatalogAction,
}: {
  readonly page: AssistantPageId;
  readonly messages: readonly StoredAssistantMessage[];
  readonly open: boolean;
  readonly position?: PanelPosition;
  readonly size?: PanelSize;
  readonly mobileHeight?: number;
  readonly loading: boolean;
  readonly enabled: boolean;
  readonly error?: string;
  readonly input: string;
  readonly deckBridge?: DeckEditorBridge;
  readonly catalogBridge?: CardCatalogBridge;
  readonly sandboxBridge?: SandboxBridge;
  readonly onInput: (value: string) => void;
  readonly onOpen: () => void;
  readonly onClose: () => void;
  readonly onClear: () => void;
  readonly onCancel: () => void;
  readonly onSubmit: (message?: string) => void;
  readonly onPosition: (position: PanelPosition) => void;
  readonly onSize: (size: PanelSize) => void;
  readonly onMobileHeight: (height: number) => void;
  readonly onCatalogAction: (action: AssistantCatalogAction) => void;
}): ReactElement {
  const panelRef = useRef<HTMLElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    offsetX: number;
    offsetY: number;
  } | undefined>(undefined);
  const resizeRef = useRef<{
    pointerId: number;
    right: number;
    bottom: number;
  } | undefined>(undefined);
  const mobileSheetRef = useRef<{
    pointerId: number;
    offsetY: number;
    currentHeight: number;
  } | undefined>(undefined);
  const touchScrollYRef = useRef<number | undefined>(undefined);
  const [mobileDragging, setMobileDragging] = useState(false);
  const [mobileDragHeight, setMobileDragHeight] = useState<number>();
  const pageKnowledge = ASSISTANT_PAGE_KNOWLEDGE[page];

  useEffect(() => {
    messagesRef.current?.scrollTo?.({
      top: messagesRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [loading, messages.length, open]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!open || panel === null) return undefined;

    const startTouchScroll = (event: TouchEvent): void => {
      touchScrollYRef.current = event.touches[0]?.clientY;
    };
    const containTouchScroll = (event: TouchEvent): void => {
      const currentY = event.touches[0]?.clientY;
      const previousY = touchScrollYRef.current;
      touchScrollYRef.current = currentY;
      if (currentY === undefined || previousY === undefined) return;

      const target = event.target instanceof Element ? event.target : undefined;
      const scrollSurface = target?.closest<HTMLElement>('.cards-ai-messages, .cards-ai-composer textarea');
      if (scrollSurface === undefined || scrollSurface === null) {
        event.preventDefault();
        return;
      }

      const deltaY = currentY - previousY;
      const maximumScrollTop = scrollSurface.scrollHeight - scrollSurface.clientHeight;
      const cannotScroll = maximumScrollTop <= 1;
      const pullingPastTop = deltaY > 0 && scrollSurface.scrollTop <= 0;
      const pullingPastBottom = deltaY < 0 && scrollSurface.scrollTop >= maximumScrollTop - 1;
      if (cannotScroll || pullingPastTop || pullingPastBottom) event.preventDefault();
    };
    const endTouchScroll = (): void => {
      touchScrollYRef.current = undefined;
    };

    panel.addEventListener('touchstart', startTouchScroll, { passive: true });
    panel.addEventListener('touchmove', containTouchScroll, { passive: false });
    panel.addEventListener('touchend', endTouchScroll, { passive: true });
    panel.addEventListener('touchcancel', endTouchScroll, { passive: true });
    return () => {
      panel.removeEventListener('touchstart', startTouchScroll);
      panel.removeEventListener('touchmove', containTouchScroll);
      panel.removeEventListener('touchend', endTouchScroll);
      panel.removeEventListener('touchcancel', endTouchScroll);
    };
  }, [open]);

  useEffect(() => {
    const move = (event: PointerEvent): void => {
      if (dragRef.current !== undefined && event.pointerId === dragRef.current.pointerId) {
        const panelSize = size ?? maximumPanelSize();
        onPosition(clampPosition({
          x: event.clientX - dragRef.current.offsetX,
          y: event.clientY - dragRef.current.offsetY,
        }, panelSize));
        return;
      }
      if (mobileSheetRef.current !== undefined
        && event.pointerId === mobileSheetRef.current.pointerId) {
        const nextHeight = Math.max(
          MOBILE_SHEET_DRAG_FLOOR,
          Math.min(
            globalThis.innerHeight - PANEL_VIEWPORT_MARGIN,
            globalThis.innerHeight - event.clientY + mobileSheetRef.current.offsetY,
          ),
        );
        mobileSheetRef.current.currentHeight = nextHeight;
        setMobileDragHeight(nextHeight);
        event.preventDefault();
        return;
      }
      if (resizeRef.current === undefined || event.pointerId !== resizeRef.current.pointerId) {
        return;
      }
      const nextSize = clampSize({
        width: resizeRef.current.right - event.clientX,
        height: resizeRef.current.bottom - event.clientY,
      });
      onSize(nextSize);
      onPosition(clampPosition({
        x: resizeRef.current.right - nextSize.width,
        y: resizeRef.current.bottom - nextSize.height,
      }, nextSize));
    };
    const end = (event: PointerEvent): void => {
      if (dragRef.current?.pointerId === event.pointerId) dragRef.current = undefined;
      if (resizeRef.current?.pointerId === event.pointerId) resizeRef.current = undefined;
      if (mobileSheetRef.current?.pointerId === event.pointerId) {
        const currentHeight = mobileSheetRef.current.currentHeight;
        mobileSheetRef.current = undefined;
        setMobileDragging(false);
        setMobileDragHeight(undefined);
        const { minimum } = mobileSheetMetrics();
        if (event.type !== 'pointercancel'
          && currentHeight < minimum - MOBILE_SHEET_DISMISS_DISTANCE) {
          onMobileHeight(defaultMobileSheetHeight());
          onClose();
        } else {
          onMobileHeight(nearestMobileSheetHeight(currentHeight));
        }
      }
    };
    globalThis.addEventListener('pointermove', move);
    globalThis.addEventListener('pointerup', end);
    globalThis.addEventListener('pointercancel', end);
    return () => {
      globalThis.removeEventListener('pointermove', move);
      globalThis.removeEventListener('pointerup', end);
      globalThis.removeEventListener('pointercancel', end);
    };
  }, [onClose, onMobileHeight, onPosition, onSize, size]);

  function startDrag(event: ReactPointerEvent<HTMLElement>): void {
    if (globalThis.matchMedia?.('(max-width: 620px)').matches) return;
    if (event.target instanceof Element
      && event.target.closest('.cards-ai-header-actions') !== null) return;
    const panel = event.currentTarget.closest<HTMLElement>('.cards-ai-panel');
    if (panel === null) return;
    const bounds = panel.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - bounds.left,
      offsetY: event.clientY - bounds.top,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function startResize(event: ReactPointerEvent<HTMLButtonElement>): void {
    if (globalThis.matchMedia?.('(max-width: 620px)').matches) return;
    const panel = event.currentTarget.closest<HTMLElement>('.cards-ai-panel');
    if (panel === null) return;
    const bounds = panel.getBoundingClientRect();
    resizeRef.current = {
      pointerId: event.pointerId,
      right: bounds.right,
      bottom: bounds.bottom,
    };
    onSize(clampSize({ width: bounds.width, height: bounds.height }));
    onPosition(clampPosition(
      { x: bounds.left, y: bounds.top },
      clampSize({ width: bounds.width, height: bounds.height }),
    ));
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function startMobileSheetDrag(event: ReactPointerEvent<HTMLButtonElement>): void {
    if (!globalThis.matchMedia?.('(max-width: 620px)').matches) return;
    const currentHeight = mobileHeight ?? defaultMobileSheetHeight();
    const sheetTop = globalThis.innerHeight - currentHeight;
    mobileSheetRef.current = {
      pointerId: event.pointerId,
      offsetY: event.clientY - sheetTop,
      currentHeight,
    };
    setMobileDragHeight(currentHeight);
    setMobileDragging(true);
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function mobileSheetKeyDown(event: KeyboardEvent<HTMLButtonElement>): void {
    if (!['ArrowUp', 'ArrowDown', 'Home', 'End', 'Escape'].includes(event.key)) return;
    event.preventDefault();
    if (event.key === 'Escape') {
      onClose();
      return;
    }
    const { points } = mobileSheetMetrics();
    const currentHeight = mobileHeight ?? defaultMobileSheetHeight();
    if (event.key === 'Home') {
      onMobileHeight(points[0] ?? currentHeight);
      return;
    }
    if (event.key === 'End') {
      onMobileHeight(points.at(-1) ?? currentHeight);
      return;
    }
    if (event.key === 'ArrowUp') {
      onMobileHeight(points.find((point) => point > currentHeight + 1)
        ?? points.at(-1)
        ?? currentHeight);
      return;
    }
    const lower = [...points].reverse().find((point) => point < currentHeight - 1);
    if (lower !== undefined) {
      onMobileHeight(lower);
      return;
    }
    onMobileHeight(defaultMobileSheetHeight());
    onClose();
  }

  function resizeKeyDown(event: KeyboardEvent<HTMLButtonElement>): void {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    const currentSize = size ?? maximumPanelSize();
    const step = event.shiftKey ? 40 : 20;
    const nextSize = clampSize({
      width: currentSize.width
        + (event.key === 'ArrowLeft' ? step : event.key === 'ArrowRight' ? -step : 0),
      height: currentSize.height
        + (event.key === 'ArrowUp' ? step : event.key === 'ArrowDown' ? -step : 0),
    });
    const currentPosition = position ?? {
      x: globalThis.innerWidth - currentSize.width - 18,
      y: globalThis.innerHeight - currentSize.height - 18,
    };
    const right = currentPosition.x + currentSize.width;
    const bottom = currentPosition.y + currentSize.height;
    onSize(nextSize);
    onPosition(clampPosition({
      x: right - nextSize.width,
      y: bottom - nextSize.height,
    }, nextSize));
  }

  function keyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    onSubmit();
  }

  if (!open) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="cards-ai-launcher"
        data-testid="assistant-launcher"
        data-onboarding="ai-assistant"
        aria-label="Открыть ИИ-помощника"
        title="ИИ-помощник"
        onClick={onOpen}
      >
        <AssistantGlyph size={22} />
        <span className="cards-ai-launcher-label">ПОМОЩНИК</span>
      </Button>
    );
  }

  const effectiveMobileHeight = mobileDragHeight ?? mobileHeight;
  const style: CSSProperties | undefined = position === undefined
    && size === undefined
    && effectiveMobileHeight === undefined
    ? undefined
    : {
        ...(position === undefined
          ? {}
          : { left: position.x, top: position.y, right: 'auto', bottom: 'auto' }),
        ...(size === undefined ? {} : { width: size.width, height: size.height }),
        ...(effectiveMobileHeight === undefined
          ? {}
          : { '--cards-ai-mobile-height': `${effectiveMobileHeight}px` }),
      };
  return (
    <section
      ref={panelRef}
      className="cards-ai-panel"
      data-testid="assistant-panel"
      aria-label="ИИ-помощник"
      style={style}
      data-mobile-dragging={mobileDragging}
    >
      <button
        type="button"
        className="cards-ai-resize"
        aria-label="Изменить размер помощника"
        title="Потяните или используйте стрелки"
        onPointerDown={startResize}
        onKeyDown={resizeKeyDown}
      />
      <button
        type="button"
        className="cards-ai-sheet-handle"
        aria-label="Изменить высоту помощника"
        title="Потяните вверх или вниз"
        onPointerDown={startMobileSheetDrag}
        onKeyDown={mobileSheetKeyDown}
      />
      <header className="cards-ai-header" onPointerDown={startDrag}>
        <div className="cards-ai-identity">
          <span className="cards-ai-mark" aria-hidden="true"><AssistantGlyph size={18} /></span>
          <div>
            <strong>Помощник</strong>
            <span>{pageKnowledge.title}</span>
          </div>
        </div>
        <div className="cards-ai-header-actions">
          <Button variant="ghost" size="sm" onClick={onClear} disabled={loading || messages.length === 0}>
            Очистить
          </Button>
          <Button variant="ghost" size="sm" aria-label="Свернуть помощника" onClick={onClose}>×</Button>
        </div>
      </header>

      <div className="cards-ai-messages" ref={messagesRef} aria-live="polite">
        {messages.length === 0 ? (
          <div className="cards-ai-welcome">
            <strong>Чем помочь?</strong>
            <p>
              Я вижу текущий раздел и автоматически подключаю доступные здесь возможности.
              История сохранится при переходе между экранами.
            </p>
            <div className="cards-ai-suggestions">
              {pageKnowledge.suggestions.map((suggestion) => (
                <Button variant="secondary" size="sm" key={suggestion} onClick={() => onSubmit(suggestion)}>
                  {suggestion}
                </Button>
              ))}
            </div>
          </div>
        ) : messages.map((message) => {
          const canFocus = message.role === 'assistant'
            && message.focusElementId !== undefined
            && message.focusElementId !== null
            && message.page === page
            && visibleElementIds(page).includes(message.focusElementId);
          const proposal = message.role === 'assistant' ? message.deckSuggestion : undefined;
          const catalogAction = message.role === 'assistant' ? message.catalogAction : undefined;
          const sandboxAction = message.role === 'assistant' ? message.sandboxAction : undefined;
          const canApplyCatalogAction = catalogAction !== undefined
            && catalogAction !== null
            && catalogBridge?.page === message.page;
          const proposalTargetsDeck = proposal !== undefined
            && proposal !== null
            && deckBridge !== undefined
            && message.deckId === deckBridge.deckId;
          const proposalSnapshot: AssistantDeckSnapshot | undefined = proposal === undefined
            || proposal === null
            ? undefined
            : {
                cardIds: proposal.cardIds as readonly CardDefId[],
                passiveId: proposal.passiveId,
              };
          const proposalAnalysis = proposalSnapshot === undefined || message.deckBase === undefined
            ? undefined
            : analyzeAssistantDeck(message.deckBase, proposalSnapshot);
          const proposalBaseIsCurrent = proposalTargetsDeck
            && message.deckBase !== undefined
            && sameAssistantDeckSnapshot(message.deckBase, {
              cardIds: deckBridge.cardIds,
              passiveId: deckBridge.passiveId,
            });
          const proposalWasApplied = proposalTargetsDeck
            && proposalSnapshot !== undefined
            && deckBridge.undo !== undefined
            && sameAssistantDeckSnapshot(proposalSnapshot, deckBridge.undo.applied);
          const proposalIsValid = proposal !== undefined
            && proposal !== null
            && validateConstructedDeck({
              cardIds: proposal.cardIds,
              passiveId: proposal.passiveId,
            }, content).ready;
          return (
            <article
              className={`cards-ai-message cards-ai-message-${message.role}`}
              key={message.id}
            >
              <div className="cards-ai-message-meta">
                {message.role === 'user' ? 'Вы' : 'Помощник'}
                {message.mode === undefined ? null : (
                  <span>{message.mode === 'deck' ? 'сборка колоды' : 'помощь по UI'}</span>
                )}
              </div>
              <div className="cards-ai-message-copy">
                {message.role === 'assistant'
                  ? <AssistantMarkdown>{message.content}</AssistantMarkdown>
                  : message.content}
              </div>
              {canFocus ? (
                <Button
                  variant="secondary"
                  size="sm"
                  className="cards-ai-inline-action"
                  onClick={() => highlight(message.page, message.focusElementId!)}
                >
                  Показать в интерфейсе
                </Button>
              ) : null}
              {canApplyCatalogAction ? (
                <Button
                  variant="secondary"
                  size="sm"
                  className="cards-ai-inline-action"
                  onClick={() => onCatalogAction(catalogAction)}
                >
                  {catalogAction.cardIds.length === 0
                    ? 'Применить поиск'
                    : catalogAction.cardIds.length === 1
                      ? 'Показать карту'
                      : 'Показать карты'}
                </Button>
              ) : null}
              {sandboxAction === undefined || sandboxAction === null ? null : (
                <Button
                  variant="primary"
                  size="sm"
                  className="cards-ai-inline-action"
                  disabled={message.page !== 'sandbox' || sandboxBridge === undefined || !sandboxBridge.canApply}
                  onClick={() => sandboxBridge?.apply(sandboxAction)}
                  data-testid="assistant-apply-sandbox"
                >
                  {sandboxBridge === undefined || message.page !== 'sandbox'
                    ? 'Откройте песочницу'
                    : sandboxBridge.canApply
                      ? sandboxAction.replace ? 'Выстроить ситуацию' : 'Применить изменения'
                      : 'Дождитесь конца действия'}
                </Button>
              )}
              {proposal === undefined || proposal === null ? null : (
                <section className="cards-ai-deck-proposal" aria-label="Предложенная колода">
                  <strong>{proposal.title}</strong>
                  <p>{proposal.summary}</p>
                  <p>
                    Пассивка: {
                      proposal.passiveId === null
                        ? 'без пассивки'
                        : content.deckPassiveCatalog.get(proposal.passiveId)?.name
                          ?? proposal.passiveId
                    }
                  </p>
                  {proposalAnalysis === undefined ? null : (
                    <section
                      className="cards-ai-deck-analysis"
                      aria-label="Предпросмотр изменений колоды"
                    >
                      <strong>Перед применением</strong>
                      <p>
                        {proposalAnalysis.slots} слотов · {proposalAnalysis.battleCards} карт в бою
                        {' · '}уникальных карт: {proposalAnalysis.uniqueCards}
                        {' · '}средняя энергия {
                          proposalAnalysis.averageEnergy === null
                            ? '—'
                            : DECK_ENERGY_FORMAT.format(proposalAnalysis.averageEnergy)
                        }
                      </p>
                      <p>
                        Типы: {proposalAnalysis.typeCounts.map(({ type, count }) =>
                          `${cardTypeLabel(type)} ×${count}`).join(', ')}
                      </p>
                      <p>
                        Кривая: {proposalAnalysis.costCurve.map(({ cost, count }) =>
                          `${cost}: ×${count}`).join(' · ')}
                      </p>
                      {proposalAnalysis.added.length === 0 ? null : (
                        <p><b>Добавится:</b> {deckChangeText(proposalAnalysis.added)}</p>
                      )}
                      {proposalAnalysis.removed.length === 0 ? null : (
                        <p><b>Уберётся:</b> {deckChangeText(proposalAnalysis.removed)}</p>
                      )}
                      {!proposalAnalysis.passiveChanged ? null : (
                        <p>
                          <b>Пассивка:</b> {
                            message.deckBase?.passiveId === null
                              ? 'без пассивки'
                              : content.deckPassiveCatalog.get(message.deckBase!.passiveId)?.name
                                ?? message.deckBase!.passiveId
                          } → {
                            proposal.passiveId === null
                              ? 'без пассивки'
                              : content.deckPassiveCatalog.get(proposal.passiveId)?.name
                                ?? proposal.passiveId
                          }
                        </p>
                      )}
                    </section>
                  )}
                  <ul>
                    {deckCounts(proposal.cardIds).map((card) => (
                      <li key={card.id}>
                        <span>{card.name}</span>
                        <b>×{card.count}</b>
                      </li>
                    ))}
                  </ul>
                  {!proposalTargetsDeck || proposalBaseIsCurrent || proposalWasApplied ? null : (
                    <p className="cards-ai-deck-stale">
                      Колода изменилась после запроса. Попросите помощника обновить предложение.
                    </p>
                  )}
                  {proposalWasApplied ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="cards-ai-apply-deck"
                      disabled={deckBridge.locked}
                      onClick={() => deckBridge.undo?.run()}
                    >
                      {deckBridge.locked ? 'Редактор занят' : 'Вернуть прежний состав'}
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      className="cards-ai-apply-deck"
                      disabled={!proposalTargetsDeck || !proposalBaseIsCurrent
                        || !proposalIsValid || deckBridge.locked}
                      onClick={() => {
                        if (!proposalTargetsDeck || !proposalBaseIsCurrent
                          || !proposalIsValid) return;
                        deckBridge.apply(
                          proposal.cardIds as readonly CardDefId[],
                          proposal.passiveId,
                        );
                      }}
                    >
                      {deckBridge === undefined || message.deckId !== deckBridge.deckId
                        ? 'Откройте исходную колоду'
                        : !proposalBaseIsCurrent
                          ? 'Запросите обновлённый состав'
                          : deckBridge.locked
                            ? 'Редактор занят'
                            : 'Применить состав'}
                    </Button>
                  )}
                </section>
              )}
            </article>
          );
        })}
        {loading ? (
          <div className="cards-ai-thinking" role="status">
            <span /><span /><span /> Думаю…
          </div>
        ) : null}
      </div>

      {error === undefined ? null : <div className="cards-ai-error" role="alert">{error}</div>}
      {!enabled ? <div className="cards-ai-error" role="status">AI сейчас на техническом обслуживании.</div> : null}
      <div className="cards-ai-composer">
        <textarea
          value={input}
          rows={2}
          maxLength={4_000}
          placeholder={page === 'deck_editor'
            ? 'Спросите про интерфейс или попросите собрать колоду…'
            : page === 'sandbox'
              ? 'Опишите ситуацию или нужную карту…'
              : 'Спросите, что делать на этом экране…'}
          aria-label="Сообщение помощнику"
          disabled={loading || !enabled}
          onChange={(event) => onInput(event.currentTarget.value)}
          onKeyDown={keyDown}
        />
        {loading ? (
          <Button variant="danger" size="sm" className="cards-ai-send" onClick={onCancel}>Стоп</Button>
        ) : (
          <Button
            variant="primary"
            size="sm"
            className="cards-ai-send"
            disabled={!enabled || input.trim().length === 0}
            onClick={() => onSubmit()}
          >
            Отправить
          </Button>
        )}
      </div>
    </section>
  );
}

export function AssistantProvider({ children }: { readonly children: ReactNode }): ReactElement {
  const initialPanel = useMemo(loadPanelState, []);
  const [messages, setMessages] = useState<readonly StoredAssistantMessage[]>(loadMessages);
  const [open, setOpen] = useState(initialPanel.open);
  const [position, setPosition] = useState<PanelPosition | undefined>(initialPanel.position);
  const [size, setSize] = useState<PanelSize | undefined>(initialPanel.size);
  const [mobileHeight, setMobileHeight] = useState<number | undefined>(
    initialPanel.mobileHeight,
  );
  const [page, setPage] = useState<AssistantPageId>(() =>
    typeof document === 'undefined' ? 'unknown' : currentPage());
  const [deckBridge, setDeckBridge] = useState<DeckEditorBridge>();
  const [catalogBridge, setCatalogBridge] = useState<CardCatalogBridge>();
  const [sandboxBridge, setSandboxBridge] = useState<SandboxBridge>();
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [error, setError] = useState<string>();
  const abortRef = useRef<AbortController | undefined>(undefined);

  useEffect(() => {
    safeSet(THREAD_STORAGE_KEY, JSON.stringify(messages.slice(-MAX_STORED_MESSAGES)));
  }, [messages]);

  useEffect(() => {
    safeSet(PANEL_STORAGE_KEY, JSON.stringify({
      open,
      ...(position === undefined ? {} : position),
      ...(size === undefined ? {} : size),
      ...(mobileHeight === undefined ? {} : { mobileHeight }),
    }));
  }, [mobileHeight, open, position, size]);

  useEffect(() => {
    let queued = false;
    const update = (): void => {
      if (queued) return;
      queued = true;
      queueMicrotask(() => {
        queued = false;
        setPage(currentPage());
      });
    };
    const observer = typeof MutationObserver === 'undefined'
      ? undefined
      : new MutationObserver(update);
    observer?.observe(document.body, { childList: true, subtree: true });
    globalThis.addEventListener('popstate', update);
    update();
    return () => {
      observer?.disconnect();
      globalThis.removeEventListener('popstate', update);
    };
  }, []);

  useEffect(() => {
    const resize = (): void => {
      const nextSize = clampSize(size ?? maximumPanelSize());
      setSize((current) => current === undefined ? undefined : nextSize);
      setPosition((current) =>
        current === undefined ? undefined : clampPosition(current, nextSize));
      setMobileHeight((current) =>
        current === undefined ? undefined : clampMobileSheetHeight(current));
    };
    globalThis.addEventListener('resize', resize);
    return () => globalThis.removeEventListener('resize', resize);
  }, [size]);

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    let active = true;
    const refresh = (): void => {
      void fetch('/api/runtime-config', { cache: 'no-store', credentials: 'same-origin' })
        .then(async (response) => response.ok ? response.json() as Promise<unknown> : undefined)
        .then((value) => {
          if (!active || typeof value !== 'object' || value === null) return;
          const candidate = (value as Record<string, unknown>).assistantEnabled;
          if (typeof candidate === 'boolean') setEnabled(candidate);
        }).catch(() => undefined);
    };
    refresh();
    const interval = globalThis.setInterval(refresh, 15_000);
    return () => { active = false; globalThis.clearInterval(interval); };
  }, []);

  const registerDeckEditor = useCallback<RegisterDeckEditor>((bridge) => {
    setDeckBridge(bridge);
  }, []);
  const registerCardCatalog = useCallback<RegisterCardCatalog>((bridge) => {
    setCatalogBridge(bridge);
  }, []);
  const registerSandbox = useCallback<RegisterSandbox>((bridge) => {
    setSandboxBridge(bridge);
  }, []);

  const updatePosition = useCallback((next: PanelPosition): void => {
    setPosition(next);
  }, []);

  const submit = useCallback((suggested?: string): void => {
    const message = (suggested ?? input).trim();
    if (message === '' || loading || !enabled) return;
    const requestPage = currentPage();
    const userMessage: StoredAssistantMessage = {
      id: id(),
      role: 'user',
      content: message,
      page: requestPage,
    };
    const nextMessages = [...messages, userMessage].slice(-MAX_STORED_MESSAGES);
    setMessages(nextMessages);
    setInput('');
    setError(undefined);
    setLoading(true);
    const controller = new AbortController();
    abortRef.current = controller;
    const requestDeck = requestPage === 'deck_editor' && deckBridge !== undefined
      ? {
          deckId: deckBridge.deckId,
          name: deckBridge.name,
          cardIds: Object.freeze([...deckBridge.cardIds]),
          passiveId: deckBridge.passiveId,
        }
      : undefined;
    const body = {
      messages: nextMessages.slice(-24).map(({ role, content: value }) => ({
        role,
        content: value,
      })),
      page: requestPage,
      visibleElementIds: visibleElementIds(requestPage),
      ...(requestDeck === undefined ? {} : { deck: requestDeck }),
      ...(requestPage === 'sandbox' && sandboxBridge !== undefined ? {
        sandbox: sandboxBridge.context,
      } : {}),
    };
    void fetch('/api/assistant/chat', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      mode: 'same-origin',
      redirect: 'error',
      cache: 'no-store',
      body: JSON.stringify(body),
      signal: controller.signal,
    }).then(async (response) => {
      let payload: unknown;
      try {
        payload = await response.json();
      } catch {
        payload = undefined;
      }
      if (!response.ok) {
        const code = typeof payload === 'object' && payload !== null
          && typeof (payload as Record<string, unknown>).error === 'string'
          ? (payload as Record<string, string>).error
          : undefined;
        throw new Error(assistantErrorMessage(response.status, code));
      }
      const result = parseAssistantChatResponse(payload);
      const assistantMessage: StoredAssistantMessage = {
        id: id(),
        role: 'assistant',
        content: result.reply.message,
        page: requestPage,
        mode: result.mode,
        focusElementId: result.reply.focusElementId,
        catalogAction: result.reply.catalogAction,
        deckSuggestion: result.reply.deckSuggestion,
        sandboxAction: result.reply.sandboxAction,
        ...(result.reply.deckSuggestion === null || requestDeck === undefined
          ? {}
          : {
              deckId: requestDeck.deckId,
              deckBase: {
                cardIds: requestDeck.cardIds,
                passiveId: requestDeck.passiveId,
              },
            }),
      };
      setMessages((current) => [...current, assistantMessage].slice(-MAX_STORED_MESSAGES));
      const catalogApplied = result.reply.catalogAction !== null
        && catalogBridge?.page === requestPage
        && currentPage() === requestPage
        && applyCatalogAction(catalogBridge, result.reply.catalogAction);
      if (!catalogApplied
        && result.reply.focusElementId !== null
        && currentPage() === requestPage) {
        highlight(requestPage, result.reply.focusElementId);
      }
    }).catch((caught: unknown) => {
      if (controller.signal.aborted) return;
      setError(caught instanceof Error
        ? caught.message
        : 'Не удалось получить ответ помощника.');
    }).finally(() => {
      if (abortRef.current === controller) abortRef.current = undefined;
      setLoading(false);
    });
  }, [catalogBridge, deckBridge, enabled, input, loading, messages, sandboxBridge]);

  const portal = typeof document === 'undefined' ? null : createPortal(
    <AssistantPanel
      page={page}
      messages={messages}
      open={open}
      loading={loading}
      enabled={enabled}
      input={input}
      {...(position === undefined ? {} : { position })}
      {...(size === undefined ? {} : { size })}
      {...(mobileHeight === undefined ? {} : { mobileHeight })}
      {...(deckBridge === undefined ? {} : { deckBridge })}
      {...(catalogBridge === undefined ? {} : { catalogBridge })}
      {...(sandboxBridge === undefined ? {} : { sandboxBridge })}
      {...(error === undefined ? {} : { error })}
      onInput={setInput}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onClear={() => {
        setMessages([]);
        setError(undefined);
      }}
      onCancel={() => abortRef.current?.abort()}
      onSubmit={submit}
      onPosition={updatePosition}
      onSize={setSize}
      onMobileHeight={setMobileHeight}
      onCatalogAction={(action) => {
        applyCatalogAction(catalogBridge, action);
      }}
    />,
    document.body,
  );

  return (
    <DeckEditorAssistantContext.Provider value={registerDeckEditor}>
      <CardCatalogAssistantContext.Provider value={registerCardCatalog}>
        <SandboxAssistantContext.Provider value={registerSandbox}>
          {children}
          {portal}
        </SandboxAssistantContext.Provider>
      </CardCatalogAssistantContext.Provider>
    </DeckEditorAssistantContext.Provider>
  );
}
